import { ISSUE_TYPE } from '../screen/GenerationPanel/Generationpanel.types';
import { VolumeLevel, VOLUME_CONTEXTS } from './volumePrompts';

export type GenerationVolumeIssueType = ISSUE_TYPE;

export interface GenerationVolumePrompt {
  systemPrompt: string;
  volumeGuidance: Record<VolumeLevel, string>;
}

export const GENERATION_VOLUME_PROMPTS: Partial<Record<GenerationVolumeIssueType, GenerationVolumePrompt>> = {
  [ISSUE_TYPE.MultipleEpics]: {
    systemPrompt: 'Generate multiple Epics that logically partition the requirements into major workstreams.',
    volumeGuidance: {
      low: 'Create around 5 well-defined epics that capture the key workstreams. Each epic should represent a significant area of functionality.',
      medium: 'Create 8-10 epics that provide thorough coverage of the requirements. Include epics for core features and supporting capabilities.',
      high: 'Create 12-16 comprehensive epics that fully decompose the initiative. Include epics for all functional areas plus cross-cutting concerns like security, performance, observability, and DevOps.'
    }
  },
  [ISSUE_TYPE.MultipleStories]: {
    systemPrompt: 'Generate User Stories that break down the requirements into deliverable increments of value.',
    volumeGuidance: {
      low: 'Generate 5-6 well-crafted stories that cover the core functionality. Each story should deliver meaningful value.',
      medium: 'Generate 6-8 stories with good granularity for sprint planning. Include stories for primary flows and key variations.',
      high: 'Generate 8-10 comprehensive stories covering all aspects. Include stories for error handling, edge cases, accessibility, and user experience polish.'
    }
  },
  [ISSUE_TYPE.EpicsAndStories]: {
    systemPrompt: 'Generate a structured hierarchy of Epics with User Stories underneath each epic.',
    volumeGuidance: {
      low: 'Create 5 epics with 4-5 stories each. Each epic should be a cohesive workstream with stories that fully deliver its scope.',
      medium: 'Create 8-10 epics with 5-6 stories each. Provide comprehensive coverage that enables detailed release planning.',
      high: 'Create 12-16 epics with 6-8 stories each. This is an enterprise-scale breakdown covering all functional areas, integrations, and non-functional requirements.'
    }
  },
  [ISSUE_TYPE.SingleEpicMultiStories]: {
    systemPrompt: 'Generate a single comprehensive Epic with multiple User Stories that fully decompose the epic\'s scope.',
    volumeGuidance: {
      low: 'The epic should have 5-6 stories providing solid coverage of the core functionality and key user journeys.',
      medium: 'The epic should have 6-8 stories with thorough breakdown including setup, core features, edge cases, and finishing touches.',
      high: 'The epic should have 8-10 stories with exhaustive coverage including all user scenarios, error states, accessibility, testing considerations, and documentation.'
    }
  },
  [ISSUE_TYPE.MultipleTasks]: {
    systemPrompt: 'Generate actionable Tasks that represent specific pieces of work to be completed.',
    volumeGuidance: {
      low: 'Create 5-6 focused tasks covering the essential implementation work. Each task should be completable in hours to a day.',
      medium: 'Create 6-8 tasks with appropriate granularity for progress tracking and team coordination.',
      high: 'Create 8-10 detailed tasks covering all implementation phases including setup, development, testing, documentation, and deployment.'
    }
  },
  [ISSUE_TYPE.EpicsAndTasks]: {
    systemPrompt: 'Generate Epics with Tasks underneath. Tasks are smaller, more tactical work items than stories.',
    volumeGuidance: {
      low: 'Create 5 epics with 5-6 tasks each. Tasks should be actionable and time-boxed for efficient execution.',
      medium: 'Create 8-10 epics with 6-8 tasks each. Balance strategic epic organization with tactical task breakdown.',
      high: 'Create 12-16 epics with 8-10 tasks each. Include tasks for all aspects: technical setup, implementation, testing, documentation, and deployment.'
    }
  },
  [ISSUE_TYPE.SingleEpicMultiTasks]: {
    systemPrompt: 'Generate a single Epic with comprehensive Tasks that break down all the work required.',
    volumeGuidance: {
      low: 'Generate 5-6 tasks covering the essential implementation steps and key deliverables.',
      medium: 'Generate 6-8 tasks with clear definitions including development, testing, integration, and review work.',
      high: 'Generate 8-10 detailed tasks covering all aspects of delivery including environment setup, implementation phases, unit testing, integration testing, documentation, and deployment.'
    }
  }
};

export const buildVolumeSystemPrompt = (
  issueType: GenerationVolumeIssueType,
  volume: VolumeLevel
): string => {
  const config = GENERATION_VOLUME_PROMPTS[issueType];
  const context = VOLUME_CONTEXTS[volume];

  if (!config) {
    return '';
  }

  return [
    context.projectContext,
    config.systemPrompt,
    config.volumeGuidance[volume],
    context.scopeGuidance,
    context.granularityHint
  ].join(' ');
};
